/* VATInvoiceSummaryPage.js — Editable quarterly VAT summary of tax invoices.

   Workflow:
   1. Pick a period (Quarter+Year preset OR custom From/To range)
   2. Click "Load Invoices" — system auto-populates the editable table
   3. Edit any cell (Client Name, TRN, Particulars, Invoice Amount, 5% VAT, Net Amount)
   4. Edit the title, period subtitle, and signature blocks
   5. Download as branded PDF or styled Excel
*/
import { useState, useCallback, useMemo, useEffect } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  FilePdf, FileXls, Plus, Trash, ArrowsClockwise, Receipt, Eye,
} from '@phosphor-icons/react';
import { useCompany } from '../../contexts/CompanyContext';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const QUARTERS = [
  { value: 'Q1', label: 'Q1 — Jan / Feb / Mar', startMonth: 0, months: ['JANUARY', 'FEBRUARY', 'MARCH'] },
  { value: 'Q2', label: 'Q2 — Apr / May / Jun', startMonth: 3, months: ['APRIL', 'MAY', 'JUNE'] },
  { value: 'Q3', label: 'Q3 — Jul / Aug / Sep', startMonth: 6, months: ['JULY', 'AUGUST', 'SEPTEMBER'] },
  { value: 'Q4', label: 'Q4 — Oct / Nov / Dec', startMonth: 9, months: ['OCTOBER', 'NOVEMBER', 'DECEMBER'] },
];

const fmtNum = (v) =>
  v === '' || v === null || v === undefined
    ? ''
    : (Number(v) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const emptyRow = (sn) => ({
  sn,
  date: '',
  client_name: '',
  trn: '',
  particulars: '',
  invoice_amount: 0,
  vat_amount: 0,
  net_amount: 0,
});

export default function VATInvoiceSummaryPage() {
  const { company } = useCompany();
  const now = new Date();
  const defaultQ = (() => {
    const m = now.getMonth();
    if (m <= 2) return 'Q1';
    if (m <= 5) return 'Q2';
    if (m <= 8) return 'Q3';
    return 'Q4';
  })();

  const [mode, setMode] = useState('quarter'); // 'quarter' | 'range'
  const [quarter, setQuarter] = useState(defaultQ);
  const [year, setYear] = useState(now.getFullYear());
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  const [rows, setRows] = useState([]);
  const [title, setTitle] = useState('');
  const [titleEdited, setTitleEdited] = useState(false);
  const [subtitle, setSubtitle] = useState('');
  const [preparedBy, setPreparedBy] = useState('');
  const [verifiedBy, setVerifiedBy] = useState('');
  const [approvedBy, setApprovedBy] = useState('');
  const [approvedDesignation, setApprovedDesignation] = useState('Managing Director');
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(null);

  // Compute period dates from quarter+year
  const period = useMemo(() => {
    if (mode === 'range') {
      return { from: fromDate, to: toDate, label: fromDate && toDate ? `${fromDate} TO ${toDate}` : '' };
    }
    const q = QUARTERS.find((x) => x.value === quarter) || QUARTERS[0];
    const start = new Date(year, q.startMonth, 1);
    const end = new Date(year, q.startMonth + 3, 0);
    const from = start.toISOString().split('T')[0];
    const to = end.toISOString().split('T')[0];
    const ordinal = { Q1: '1st', Q2: '2nd', Q3: '3rd', Q4: '4th' }[quarter];
    const label = `${ordinal} QUARTER ${year} (${q.months.join(', ')} ${year})`;
    return { from, to, label };
  }, [mode, quarter, year, fromDate, toDate]);

  // Auto-derive default title from company + period (until the user manually edits it)
  useEffect(() => {
    if (titleEdited) return;
    const companyName = (company?.name || 'COMPANY').toUpperCase();
    setTitle(`${companyName} TAX INVOICES FOR ${period.label}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company?.name, period.label]);

  const loadInvoices = useCallback(async () => {
    if (!period.from || !period.to) {
      toast.error('Please choose a period or date range first.');
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/reports/vat-invoice-summary`, {
        params: { from: period.from, to: period.to },
      });
      setRows(data.rows || []);
      // Refresh title with the chosen period whenever we load (unless user edited)
      if (!titleEdited) {
        const companyName = (company?.name || 'COMPANY').toUpperCase();
        setTitle(`${companyName} TAX INVOICES FOR ${period.label}`);
      }
      toast.success(`Loaded ${data.count} invoice${data.count === 1 ? '' : 's'} for the period`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load VAT summary');
    } finally {
      setLoading(false);
    }
  }, [period, company]);

  // Auto-load on first mount
  useEffect(() => { loadInvoices(); /* eslint-disable-next-line */ }, []);

  const updateRow = (idx, field, value) => {
    setRows((rs) => {
      const copy = [...rs];
      copy[idx] = { ...copy[idx], [field]: value };
      // Auto-compute net = invoice - VAT when user edits invoice or vat
      if (field === 'invoice_amount' || field === 'vat_amount') {
        const inv = parseFloat(field === 'invoice_amount' ? value : copy[idx].invoice_amount) || 0;
        const vat = parseFloat(field === 'vat_amount' ? value : copy[idx].vat_amount) || 0;
        copy[idx].net_amount = +(inv - vat).toFixed(2);
      }
      return copy;
    });
  };

  const addBlankRow = () => {
    setRows((rs) => [...rs, emptyRow(rs.length + 1)]);
  };
  const removeRow = (idx) => {
    setRows((rs) => rs.filter((_, i) => i !== idx).map((r, i) => ({ ...r, sn: i + 1 })));
  };

  // Totals
  const totals = useMemo(() => {
    return rows.reduce(
      (acc, r) => {
        acc.invoice += parseFloat(r.invoice_amount) || 0;
        acc.vat += parseFloat(r.vat_amount) || 0;
        acc.net += parseFloat(r.net_amount) || 0;
        return acc;
      },
      { invoice: 0, vat: 0, net: 0 },
    );
  }, [rows]);

  const buildPayload = () => ({
    title: title.trim(),
    subtitle: subtitle.trim(),
    period_label: period.label,
    rows: rows.map((r, i) => ({
      sn: i + 1,
      date: r.date || '',
      client_name: r.client_name || '',
      trn: r.trn || '',
      particulars: r.particulars || '',
      invoice_amount: parseFloat(r.invoice_amount) || 0,
      vat_amount: parseFloat(r.vat_amount) || 0,
      net_amount: parseFloat(r.net_amount) || 0,
    })),
    prepared_by: preparedBy.trim(),
    verified_by: verifiedBy.trim(),
    approved_by: approvedBy.trim(),
    approved_designation: approvedDesignation.trim(),
  });

  const exportFile = async (kind) => {
    if (!rows.length) {
      toast.error('No rows to export. Load invoices or add rows first.');
      return;
    }
    setDownloading(kind);
    try {
      const res = await axios.post(
        `${API_URL}/reports/vat-invoice-summary/${kind}`,
        buildPayload(),
        { responseType: 'blob' },
      );
      const ext = kind === 'excel' ? 'xlsx' : kind;
      const blobUrl = window.URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = `VAT_Invoice_Summary_${period.label.replace(/[\s/]+/g, '_').slice(0, 40)}.${ext}`;
      a.click();
      window.URL.revokeObjectURL(blobUrl);
      toast.success(`Downloaded ${ext.toUpperCase()}`);
    } catch (e) {
      toast.error('Download failed');
    } finally {
      setDownloading(null);
    }
  };

  const viewPdf = async () => {
    if (!rows.length) {
      toast.error('No rows to preview. Load invoices or add rows first.');
      return;
    }
    try {
      const res = await axios.post(
        `${API_URL}/reports/vat-invoice-summary/pdf?inline=true`,
        buildPayload(),
        { responseType: 'blob' },
      );
      const blob = new Blob([res.data], { type: 'application/pdf' });
      window.open(window.URL.createObjectURL(blob), '_blank');
    } catch (e) {
      toast.error('Preview failed');
    }
  };

  return (
    <div className="space-y-6" data-testid="vat-invoice-summary-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Receipt size={24} className="text-rose-600" />
            VAT Tax Invoice Summary
          </h1>
          <p className="text-sm text-muted-foreground">
            Editable quarterly summary of tax invoices for VAT computation and filing.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={viewPdf} data-testid="vat-summary-preview">
            <Eye size={16} className="mr-1.5" /> Preview PDF
          </Button>
          <Button variant="outline" onClick={() => exportFile('pdf')} disabled={downloading === 'pdf'} data-testid="vat-summary-pdf">
            <FilePdf size={16} className="mr-1.5" /> {downloading === 'pdf' ? 'Generating…' : 'Download PDF'}
          </Button>
          <Button variant="outline" onClick={() => exportFile('excel')} disabled={downloading === 'excel'} data-testid="vat-summary-excel">
            <FileXls size={16} className="mr-1.5" /> {downloading === 'excel' ? 'Generating…' : 'Download Excel'}
          </Button>
        </div>
      </div>

      {/* Period selector */}
      <Card className="p-4">
        <div className="grid grid-cols-1 lg:grid-cols-6 gap-3 items-end">
          <div>
            <Label className="text-xs">Mode</Label>
            <Select value={mode} onValueChange={setMode}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="quarter">Quarter + Year</SelectItem>
                <SelectItem value="range">Custom Date Range</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {mode === 'quarter' ? (
            <>
              <div>
                <Label className="text-xs">Quarter</Label>
                <Select value={quarter} onValueChange={setQuarter}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {QUARTERS.map((q) => (
                      <SelectItem key={q.value} value={q.value}>{q.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Year</Label>
                <Input
                  type="number"
                  value={year}
                  onChange={(e) => setYear(parseInt(e.target.value || now.getFullYear()))}
                />
              </div>
            </>
          ) : (
            <>
              <div>
                <Label className="text-xs">From</Label>
                <Input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
              </div>
              <div>
                <Label className="text-xs">To</Label>
                <Input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
              </div>
            </>
          )}
          <div className="lg:col-span-2 text-xs text-muted-foreground">
            <div className="font-semibold uppercase tracking-wide mb-1">Resolved period</div>
            <div className="font-mono">{period.from || '—'} → {period.to || '—'}</div>
            <div className="mt-1">{period.label}</div>
          </div>
          <Button onClick={loadInvoices} disabled={loading} data-testid="vat-summary-load">
            <ArrowsClockwise size={16} className={`mr-1.5 ${loading ? 'animate-spin' : ''}`} />
            {loading ? 'Loading…' : 'Load Invoices'}
          </Button>
        </div>
      </Card>

      {/* Editable title + subtitle */}
      <Card className="p-4 space-y-3">
        <div>
          <Label className="text-xs">Title (editable)</Label>
          <Input value={title} onChange={(e) => { setTitle(e.target.value); setTitleEdited(true); }} className="font-bold text-rose-700" />
        </div>
        <div>
          <Label className="text-xs">Subtitle / Notes (optional)</Label>
          <Input value={subtitle} onChange={(e) => setSubtitle(e.target.value)} />
        </div>
      </Card>

      {/* Editable rows table */}
      <Card className="overflow-hidden">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-xs uppercase">
                <tr>
                  <th className="px-2 py-2 text-left w-10">S/N</th>
                  <th className="px-2 py-2 text-left w-32">Date</th>
                  <th className="px-2 py-2 text-left">Client Name</th>
                  <th className="px-2 py-2 text-left w-40">TRN</th>
                  <th className="px-2 py-2 text-left w-56">Particulars</th>
                  <th className="px-2 py-2 text-right w-32">Invoice Amount</th>
                  <th className="px-2 py-2 text-right w-28">5% VAT</th>
                  <th className="px-2 py-2 text-right w-32">Net Amount</th>
                  <th className="px-2 py-2 w-12"></th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="px-4 py-8 text-center text-muted-foreground text-sm">
                      No rows. Load invoices for a period or click "+ Add Row" below.
                    </td>
                  </tr>
                ) : (
                  rows.map((r, idx) => (
                    <tr key={idx} className="border-t hover:bg-muted/20">
                      <td className="px-2 py-1 text-center font-mono text-xs">{idx + 1}</td>
                      <td className="px-2 py-1">
                        <Input type="date" value={r.date} onChange={(e) => updateRow(idx, 'date', e.target.value)} className="h-8 text-xs" />
                      </td>
                      <td className="px-2 py-1">
                        <Input value={r.client_name} onChange={(e) => updateRow(idx, 'client_name', e.target.value)} className="h-8 text-xs" />
                      </td>
                      <td className="px-2 py-1">
                        <Input value={r.trn} onChange={(e) => updateRow(idx, 'trn', e.target.value)} className="h-8 text-xs font-mono" />
                      </td>
                      <td className="px-2 py-1">
                        <Input value={r.particulars} onChange={(e) => updateRow(idx, 'particulars', e.target.value)} className="h-8 text-xs" />
                      </td>
                      <td className="px-2 py-1">
                        <Input type="number" step="0.01" value={r.invoice_amount} onChange={(e) => updateRow(idx, 'invoice_amount', e.target.value)} className="h-8 text-xs text-right font-mono" />
                      </td>
                      <td className="px-2 py-1">
                        <Input type="number" step="0.01" value={r.vat_amount} onChange={(e) => updateRow(idx, 'vat_amount', e.target.value)} className="h-8 text-xs text-right font-mono" />
                      </td>
                      <td className="px-2 py-1">
                        <Input type="number" step="0.01" value={r.net_amount} onChange={(e) => updateRow(idx, 'net_amount', e.target.value)} className="h-8 text-xs text-right font-mono" />
                      </td>
                      <td className="px-2 py-1 text-center">
                        <Button size="icon" variant="ghost" onClick={() => removeRow(idx)} className="h-7 w-7" data-testid={`vat-summary-row-${idx}-delete`}>
                          <Trash size={14} className="text-rose-600" />
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
              <tfoot className="bg-muted/30 font-bold text-sm border-t-2">
                <tr>
                  <td colSpan={5} className="px-2 py-2 text-right uppercase">Total</td>
                  <td className="px-2 py-2 text-right font-mono">{fmtNum(totals.invoice)}</td>
                  <td className="px-2 py-2 text-right font-mono">{fmtNum(totals.vat)}</td>
                  <td className="px-2 py-2 text-right font-mono">{fmtNum(totals.net)}</td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </div>
          <div className="p-3 border-t flex items-center justify-between">
            <Button size="sm" variant="outline" onClick={addBlankRow} data-testid="vat-summary-add-row">
              <Plus size={14} className="mr-1.5" /> Add Row
            </Button>
            <div className="text-xs text-muted-foreground">
              Edit any cell freely. Net Amount auto-recomputes when you edit Invoice Amount or VAT.
            </div>
          </div>
        </motion.div>
      </Card>

      {/* Signature blocks */}
      <Card className="p-4">
        <div className="text-xs uppercase tracking-wide font-semibold text-muted-foreground mb-3">Signatures (printed on PDF / Excel)</div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div>
            <Label className="text-xs">Prepared By</Label>
            <Input value={preparedBy} onChange={(e) => setPreparedBy(e.target.value)} placeholder="e.g. Accountant Name / Date" />
          </div>
          <div>
            <Label className="text-xs">Verified By</Label>
            <Input value={verifiedBy} onChange={(e) => setVerifiedBy(e.target.value)} placeholder="e.g. Auditor / Reviewer" />
          </div>
          <div className="space-y-2">
            <div>
              <Label className="text-xs">Noted &amp; Approved by</Label>
              <Input value={approvedBy} onChange={(e) => setApprovedBy(e.target.value)} placeholder="e.g. SHAFAQAT ALI GHUMMAN" />
            </div>
            <div>
              <Label className="text-xs">Designation</Label>
              <Input value={approvedDesignation} onChange={(e) => setApprovedDesignation(e.target.value)} placeholder="Managing Director" />
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
}
